import { useFormikContext } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { Col } from "reactstrap";
import GoogleMaps from "../GoogleMaps";
import CustomFileInputProps from "./FileInput";
import CustomInput from "./Input";
import CustomToggle from "src/components/Toggle";
import CustomSelectInput from "./SelectInput";
import { InputKey } from "./type";
import TextEditor from "./HtmlEditor";

const GetInput = ({
  inputprop,
  disabledvalue = false,
  value,
  error,
  touch,
  overrideOnChange,
}: {
  error?: any;
  touch?: any;
  value?: any;
  inputprop: InputKey;
  disabledvalue?: boolean;
  overrideOnChange?: (e: any, values: any, setFieldValue?: any) => void;
}) => {
  const { touched, errors, values, handleBlur, setFieldValue } =
    useFormikContext<any>();
  const { t } = useTranslation();

  const isSelect = inputprop.type === "select";
  const isSwitch = inputprop.type === "switch";
  const isFile = inputprop.type === "file";
  const isEditor = !!inputprop.isEditor;
  const isMap = !!inputprop.googlemap;
  const isPlain = !(isEditor || isFile || isSelect || isSwitch) && !isMap;

  const ph =
    inputprop.placeholder ??
    (inputprop.label ? inputprop.label : "");

  // Bölüm başlığı (form gruplama) — reusable
  if ((inputprop.type as string) === "section") {
    return (
      <Col md={12}>
        <div className="m-form-section">
          <span className="m-form-section__title">{inputprop.label}</span>
        </div>
      </Col>
    );
  }

  return (
    <Col md={inputprop.col ?? 12}>
      {/* SELECT (tek / çoklu) */}
      {isSelect && inputprop.lookup && (
        <CustomSelectInput
          isMulti={inputprop.multiple}
          disabled={disabledvalue}
          label={inputprop.label}
          className={inputprop.className}
          placement={inputprop.placement}
          placeholder={
            inputprop.placeholder
              ? inputprop.placeholder
              : inputprop.label
              ? t("InputComponent.Select.Placeholder", {
                  key: inputprop.label,
                }).toString()
              : ""
          }
          renderValue={inputprop.lookup.renderValue}
          options={inputprop.lookup.options}
          labelKey={inputprop.lookup.labelKey}
          valueKey={inputprop.lookup.valueKey}
          error={error ? error : errors[inputprop.field]}
          touched={touch ? touch : touched[inputprop.field]}
          value={value ? value : values[inputprop.field]}
          onChange={(e) => {
            return setFieldValue(`${inputprop.field}`, e);
          }}
        />
      )}

      {/* SWITCH → temiz toggle satırı */}
      {isSwitch && (
        <div
          className={"m-toggle-field" + (disabledvalue ? " is-disabled" : "")}
        >
          <span className="m-toggle-field__label">{inputprop.label}</span>
          <CustomToggle
            value={value ? value : values[inputprop.field]}
            disabled={disabledvalue}
            onChange={(e) => {
              if (overrideOnChange) {
                overrideOnChange(e, values, setFieldValue);
              } else {
                return setFieldValue(`${inputprop.field}`, e);
              }
            }}
            {...inputprop.toggle}
          />
        </div>
      )}

      {/* HARİTA */}
      {isMap && inputprop.googlemap && (
        <div className="m-field">
          {inputprop.label && (
            <label className="m-field__label">{inputprop.label}</label>
          )}
          <GoogleMaps
            lat={parseFloat(values[inputprop.googlemap.latField])}
            lng={parseFloat(values[inputprop.googlemap.lonField])}
            setLat={(lat) =>
              setFieldValue(
                inputprop.googlemap ? inputprop.googlemap.latField : "",
                lat
              )
            }
            setLng={(lng) =>
              setFieldValue(
                inputprop.googlemap ? inputprop.googlemap.lonField : "",
                lng
              )
            }
          />
        </div>
      )}

      {/* DOSYA */}
      {isFile && (
        <CustomFileInputProps
          multiple={inputprop.multiple}
          currentFileLinks={inputprop.fileField && values[inputprop.fileField]}
          type={inputprop.type}
          disabled={disabledvalue}
          field={inputprop.field}
          label={inputprop.label}
          accept={inputprop.accept}
          placeholder={ph}
          error={error ? error : errors[inputprop.field]}
          touched={touch ? touch : touched[inputprop.field]}
          value={value ? value : values[inputprop.field]}
          valueRenderer={() =>
            inputprop.fileValueRenderer &&
            inputprop.fileValueRenderer(values, setFieldValue)
          }
          onChange={(e) => {
            if (overrideOnChange) {
              overrideOnChange(e, values, setFieldValue);
            } else {
              return setFieldValue(`${inputprop.field}`, e);
            }
          }}
        />
      )}

      {/* METİN / SAYI / TARİH vb. */}
      {isPlain && (
        <CustomInput
          type={inputprop.type}
          disabled={disabledvalue}
          field={inputprop.field}
          name={inputprop.field}
          label={inputprop.label}
          placeholder={ph}
          error={error ? error : errors[inputprop.field]}
          touched={touch ? touch : touched[inputprop.field]}
          value={value ? value : values[inputprop.field]}
          onChange={(e) => {
            if (overrideOnChange) {
              overrideOnChange(e, values, setFieldValue);
            } else {
              return setFieldValue(`${inputprop.field}`, e.target.value);
            }
          }}
        />
      )}

      {/* WYSIWYG */}
      {isEditor && (
        <TextEditor
          disabled={disabledvalue}
          field={inputprop.field}
          label={inputprop.label}
          placeholder={ph}
          error={error ? error : errors[inputprop.field]}
          touched={touch ? touch : touched[inputprop.field]}
          value={value ? value : values[inputprop.field]}
          setFieldValue={(e) => {
            if (overrideOnChange) {
              overrideOnChange(e, values, setFieldValue);
            } else {
              return setFieldValue(`${inputprop.field}`, e);
            }
          }}
        />
      )}
    </Col>
  );
};

export default GetInput;
